/**
 * TermsPage — Termeni și condiții pentru OneFDP.
 *
 * Bază: termenii de pe fabricadeprofile.ro (Weasel Art SRL, RO20741904)
 * adaptați pentru platforma SaaS de vizualizare AI cu profile decorative
 * FDP. Elimin secțiunile de vânzare fizică (livrare, retur produse fizice)
 * și adaug secțiuni relevante pentru platforma AI (utilizarea AI, imagini
 * cu rol orientativ, prelucrare date personale conform GDPR).
 *
 * Rută: /termeni-si-conditii + alias /terms
 */
import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background grain" data-testid="terms-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 text-sm mb-8 transition"
          data-testid="terms-back-link"
        >
          <ArrowLeft size={14} /> Înapoi la pagina principală
        </Link>

        <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3">
          Document legal · actualizat la {new Date().toLocaleDateString("ro-RO")}
        </div>
        <h1 className="font-outfit font-light text-white text-4xl md:text-5xl tracking-tighter mb-4">
          Termeni și condiții
        </h1>
        <p className="text-zinc-400 text-sm mb-10">
          Aceștia sunt termenii pe baza cărora vă punem la dispoziție platforma
          onefdp.com. Vă rugăm să îi citiți cu atenție înainte de a utiliza
          serviciul.
        </p>

        <article className="space-y-10 text-zinc-300 leading-relaxed font-manrope">
          <Section num="1" title="INFORMAȚII GENERALE">
            <p>
              Această pagină vă prezintă termenii și condițiile pe baza cărora
              vă punem la dispoziție serviciul de vizualizare AI a profilelor
              decorative FDP prin intermediul platformei online{" "}
              <strong className="text-orange-400">onefdp.com</strong>{" "}
              (denumită în continuare &bdquo;Platforma&rdquo;, &bdquo;OneFDP&rdquo; sau &bdquo;Serviciul&rdquo;).
              Vă rugăm să citiți cu atenție acești termeni și condiții înainte
              de a utiliza Platforma.
            </p>
            <p>
              Site-ul <strong>onefdp.com</strong> este un serviciu digital
              complementar site-ului{" "}
              <a
                href="https://www.fabricadeprofile.ro"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 underline decoration-orange-500/40 underline-offset-4 hover:text-orange-300"
              >
                fabricadeprofile.ro
              </a>{" "}
              și aparține companiei{" "}
              <strong className="text-white">Weasel Art SRL</strong>{" "}
              (denumită în continuare &bdquo;Compania&rdquo; sau &bdquo;FDP&rdquo;), societate ce poate
              fi contactată la:
            </p>
            <ul className="list-none space-y-1 pl-4 border-l border-orange-500/30 text-sm">
              <li>Email: <a href="mailto:office@fabricadeprofile.ro" className="text-orange-400 hover:text-orange-300">office@fabricadeprofile.ro</a></li>
              <li>Cod unic de înregistrare: RO20741904</li>
              <li>Număr de ordine în registrul comerțului: J40/1165/2007</li>
              <li>Sediu social: Str. Unității, Nr. 1, Balotești, Ilfov</li>
            </ul>
            <p>
              Accesarea Platformei OneFDP presupune acceptarea în întregime a
              Termenilor și condițiilor de utilizare. Recomandăm citirea cu
              atenție a acestora. De asemenea, crearea unui cont sau lansarea
              unei randări presupune acordul dumneavoastră cu privire la
              termenii aplicabili serviciilor furnizate de Weasel Art SRL prin
              intermediul Platformei. Ne rezervăm dreptul de a modifica în
              orice moment Termenii și condițiile, fără o notificare
              prealabilă. Cea mai recentă versiune poate fi accesată în această
              pagină.
            </p>
          </Section>

          <Section num="2" title="OBIECTUL SERVICIULUI">
            <p>
              OneFDP este o platformă software care oferă utilizatorilor
              posibilitatea de a genera{" "}
              <strong className="text-orange-400">
                simulări vizuale (randări) fotorealiste
              </strong>{" "}
              ale unor imobile (fațade, interioare) cu profile decorative FDP
              aplicate, folosind tehnologii de inteligență artificială
              generativă (AI). Rezultatele sunt destinate exclusiv{" "}
              <strong>scopurilor orientative</strong> de vizualizare și
              inspirație în procesul de design.
            </p>
            <p>
              Imaginile generate de Platformă:
            </p>
            <ul className="list-disc pl-6 space-y-1 marker:text-orange-500">
              <li>
                Pot conține mici diferențe, distorsiuni sau erori față de
                situația reală de la fața locului.
              </li>
              <li>
                NU creează obligații contractuale între utilizator și Weasel
                Art SRL.
              </li>
              <li>
                NU pot substitui expertiza, consultanța sau măsurătorile
                specialiștilor FDP.
              </li>
              <li>
              Sunt puse la dispoziție &bdquo;ca atare&rdquo; ({"as-is"}), fără garanții
                privind acuratețea sau adecvarea la un scop specific.
              </li>
            </ul>
          </Section>

          <Section num="3" title="PROPRIETATE INTELECTUALĂ">
            <p>
              Întregul conținut al Platformei OneFDP este protejat conform
              legii dreptului de autor și legilor privind drepturile de
              proprietate intelectuală. Conținutul Platformei, incluzând, dar
              nelimitându-se la logo, marcă comercială, reprezentări stilizate,
              simboluri, imagini, fotografii, texte, cod sursă, algoritmi și
              interfețe, este proprietatea exclusivă a WEASEL ART SRL.
            </p>
            <p>
              Orice folosire, parțială sau integrală, fără acordul scris al
              WEASEL ART SRL, a conținutului Platformei constituie o încălcare
              a Legii nr. 8/1996 privind dreptul de autor și drepturile
              conexe, cu modificările și completările ulterioare.
            </p>
            <p>
              <strong className="text-white">
                Drepturi asupra imaginilor generate:
              </strong>{" "}
              utilizatorii au dreptul de a utiliza imaginile generate de
              Platformă în scop personal și non-comercial. Utilizarea în
              publicații, materiale de marketing sau proiecte comerciale
              necesită acordul prealabil scris al Weasel Art SRL.
            </p>
          </Section>

          <Section num="4" title="ÎNREGISTRAREA CA UTILIZATOR ȘI CONT">
            <p>
              Pentru accesarea Platformei este necesară crearea unui cont. Vi
              se va solicita să introduceți: nume, adresă de email validă și o
              parolă. Aveți obligația de a introduce o adresă de email validă,
              în caz contrar nu va fi posibilă continuarea procedurii de
              înregistrare.
            </p>
            <p>
              Sunteți pe deplin responsabil pentru păstrarea în siguranță a
              parolei de acces precum și a oricărei informații privind
              înregistrarea, autentificarea și contul. Sunteți pe deplin
              responsabil pentru orice acțiune efectuată prin utilizarea
              Platformei din contul dumneavoastră.
            </p>
            <p>
              Prin crearea contului, vă exprimați acordul implicit ca un
              reprezentant Weasel Art SRL să vă contacteze prin orice mijloc
              disponibil (email, telefon, WhatsApp) în scopuri legate de
              gestionarea contului, ofertare, campanii comerciale sau
              recomandări de produse.
            </p>
          </Section>

          <Section num="5" title="UTILIZAREA PLATFORMEI ȘI A IA">
            <p>
              Prin utilizarea Platformei, sunteți de acord să nu:
            </p>
            <ul className="list-disc pl-6 space-y-1 marker:text-orange-500">
              <li>
                Încărcați fotografii care conțin conținut ilegal, ofensator,
                pornografic, defăimător sau care încalcă drepturi de autor ale
                terților.
              </li>
              <li>
                Utilizați Platforma pentru scopuri frauduloase sau ilegale.
              </li>
              <li>
                Efectuați reverse engineering, scraping automat sau alte
                încercări de a extrage prompturile, modelele AI sau algoritmii
                interni ai Platformei.
              </li>
              <li>
                Suprasolicitați deliberat serverele prin cereri automate care
                depășesc limitele stabilite pe cont.
              </li>
              <li>
                Distribuiți parola contului dumneavoastră către terțe persoane.
              </li>
            </ul>
            <p>
              Ne rezervăm dreptul de a suspenda sau restricționa accesul la
              Platformă în cazul încălcării acestor prevederi, fără
              notificare prealabilă și fără obligații de despăgubire.
            </p>
          </Section>

          <Section num="6" title="TARIFE ȘI PLĂȚI">
            <p>
              Utilizarea de bază a Platformei OneFDP este{" "}
              <strong className="text-orange-400">gratuită</strong>, în
              limitele stabilite prin planul de utilizare al fiecărui rol de
              utilizator (partener, sales, client final). Weasel Art SRL își
              rezervă dreptul de a introduce în viitor planuri de utilizare
              contra cost, cu notificare prealabilă a utilizatorilor existenți.
            </p>
            <p>
              Pentru serviciile complementare (comandă produse FDP, consultanță
              extinsă, proiecte personalizate), plata se efectuează separat pe
              site-ul{" "}
              <a
                href="https://www.fabricadeprofile.ro"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 underline decoration-orange-500/40 underline-offset-4 hover:text-orange-300"
              >
                fabricadeprofile.ro
              </a>{" "}
              conform termenilor de acolo.
            </p>
          </Section>

          <Section num="7" title="RĂSPUNDEREA CONTRACTUALĂ">
            <p>
              Utilizatorii Platformei OneFDP utilizează serviciul{" "}
              <strong>pe risc propriu</strong>. Weasel Art SRL nu acordă
              garanție pentru utilizarea Platformei, neputând garanta că
              serverele de găzduire nu conțin viruși sau că serviciul va fi
              disponibil în orice moment.
            </p>
            <p>
              Weasel Art SRL NU poate fi făcut responsabil pentru:
            </p>
            <ul className="list-disc pl-6 space-y-1 marker:text-orange-500">
              <li>
                Diferențe, distorsiuni sau erori între imaginile generate cu AI
                și rezultatul final de montaj efectiv la client.
              </li>
              <li>
                Decizii luate de utilizator pe baza randărilor Platformei, fără
                consultarea unui specialist FDP.
              </li>
              <li>
                Pierderi financiare, indirecte, directe sau necâștig determinate
                de utilizarea Platformei.
              </li>
              <li>
                Întreruperi temporare ale serviciului cauzate de mentenanță,
                actualizări sau evenimente independente de voința noastră.
              </li>
              <li>
                Prejudicii aduse calculatorului dumneavoastră ca urmare a
                accesării Platformei.
              </li>
            </ul>
            <p>
              În niciuo situație răspunderea Weasel Art SRL nu va depăși
              contravaloarea abonamentului plătit efectiv de dumneavoastră în
              ultimele 12 luni pentru Platformă (dacă este cazul).
            </p>
          </Section>

          <Section num="8" title="PRELUCRAREA DATELOR PERSONALE (GDPR)">
            <p>
              Weasel Art SRL respectă dispozițiile Regulamentului General
              privind Protecția Datelor (Regulamentul UE 2016/679, &bdquo;GDPR&rdquo;) și
              legislația națională aplicabilă în materia protecției datelor
              personale.
            </p>
            <p>
              Datele personale colectate prin Platformă (nume, email, telefon
              opțional, adresă IP, fotografii încărcate) sunt utilizate
              exclusiv pentru:
            </p>
            <ul className="list-disc pl-6 space-y-1 marker:text-orange-500">
              <li>Furnizarea serviciului de vizualizare AI.</li>
              <li>Comunicare directă legată de contul dumneavoastră.</li>
              <li>Statistici agregate anonimizate (pixel tracking cu IP hash-uit).</li>
              <li>Îmbunătățirea calității randărilor prin feedback opțional.</li>
            </ul>
            <p>
              Datele nu sunt vândute sau partajate cu terțe părți în scopuri
              comerciale. Aveți dreptul, conform GDPR, să solicitați accesul,
              rectificarea, ștergerea sau portabilitatea datelor dumneavoastră
              prin email la{" "}
              <a
                href="mailto:office@fabricadeprofile.ro"
                className="text-orange-400 hover:text-orange-300"
              >
                office@fabricadeprofile.ro
              </a>
              .
            </p>
            <p>
              Pentru detalii complete, consultați și Politica de
              Confidențialitate a Weasel Art SRL.
            </p>
          </Section>

          <Section num="9" title="COOKIES">
            <p>
              Platforma OneFDP utilizează cookie-uri strict necesare pentru
              funcționarea sa (autentificare, sesiune), precum și cookie-uri
              opționale pentru analiză statistică anonimizată (pixel tracking
              1x1). Nu folosim cookie-uri de publicitate direcționată sau
              re-targeting.
            </p>
            <p>
              La prima vizită, veți fi întrebat prin bara de cookie-uri dacă
              acceptați setările implicite. Puteți gestiona preferințele
              oricând.
            </p>
          </Section>

          <Section num="10" title="FORȚA MAJORĂ">
            <p>
              Nici Weasel Art SRL, nici dumneavoastră nu veți fi responsabili
              pentru orice încălcare a acestor termeni și condiții determinată
              de o situație de forță majoră, așa cum este definită în Codul
              Civil român. Partea care invocă forța majoră este obligată să
              notifice cealaltă parte în termen de 5 (cinci) zile.
            </p>
          </Section>

          <Section num="11" title="LEGISLAȚIA ȘI JURISDICȚIA APLICABILĂ">
            <p>
              Orice dispută cu privire la utilizarea Platformei onefdp.com va
              fi guvernată de legislația română în vigoare și va fi adresată
              instanțelor competente din România (Ilfov).
            </p>
            <p>
              Orice neînțelegere între noi și dumneavoastră în legătură cu
              acești termeni și condiții va fi soluționată amiabil. În cazul în
              care nu reușim să ajungem la un acord în termen de 30 de zile de
              la primirea unei notificări scrise din partea dumneavoastră,
              părțile vor supune chestiunea judecății în fața instanței
              competente de la sediul nostru social.
            </p>
          </Section>

          <Section num="12" title="PROCEDURA DE REZOLVARE A RECLAMAȚIILOR">
            <p>
              Pentru orice informații referitoare la Platformă sau orice
              solicitare/neclaritate ne puteți scrie la{" "}
              <a
                href="mailto:office@fabricadeprofile.ro"
                className="text-orange-400 hover:text-orange-300"
              >
                office@fabricadeprofile.ro
              </a>
              .
            </p>
            <p>
              Nemulțumirea dumneavoastră va fi înregistrată și veți primi un
              răspuns în scris, pe adresa de email menționată, în termen de cel
              mult 3 zile lucrătoare.
            </p>
          </Section>

          <Section num="13" title="MODIFICĂRI ALE TERMENILOR ȘI CONDIȚIILOR">
            <p>
              WEASEL ART SRL își rezervă dreptul de a efectua schimbări în
              orice moment cu privire la conținutul Platformei OneFDP,
              termenii și condițiile de utilizare, fără a fi necesară o
              notificare prealabilă către utilizatori. Modificările vor deveni
              aplicabile de la data publicării pe onefdp.com.
            </p>
            <p>
              Utilizatorul Platformei are obligația de a verifica în mod
              regulat termenii și condițiile de utilizare. Dacă utilizatorul
              continuă să folosească Platforma, se consideră că este de acord
              cu modificările efectuate.
            </p>
          </Section>

          <div className="pt-8 border-t border-white/10 text-xs text-zinc-500 space-y-1">
            <p>Ultima actualizare: {new Date().toLocaleDateString("ro-RO", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p>© Weasel Art SRL · toate drepturile rezervate · CUI RO20741904 · J40/1165/2007</p>
          </div>
        </article>
      </main>
    </div>
  );
}

function Section({ num, title, children }) {
  return (
    <section data-testid={`terms-section-${num}`}>
      <h2 className="font-outfit font-semibold text-white text-xl md:text-2xl mb-4 tracking-tight">
        <span className="text-orange-500 mr-3">{num}.</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm md:text-base">{children}</div>
    </section>
  );
}
